import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

function AgentTable({ agents, onDelete }) {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState('last_seen');
  const [sortDirection, setSortDirection] = useState('desc');

  const getOsIcon = (osType) => {
    switch (osType?.toLowerCase()) {
      case 'windows': return '🪟';
      case 'linux': return '🐧';
      case 'darwin':
      case 'macos': return '🍎';
      default: return '💻';
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAgents = useMemo(() => {
    return [...agents].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle special cases
      if (sortField === 'status') {
        aVal = a.is_online ? 1 : 0;
        bVal = b.is_online ? 1 : 0;
      } else if (sortField === 'country') {
        aVal = `${a.country_code || ''} ${a.city || ''}`;
        bVal = `${b.country_code || ''} ${b.city || ''}`;
      } else if (sortField === 'last_seen') {
        aVal = a.last_seen ? new Date(a.last_seen).getTime() : 0;
        bVal = b.last_seen ? new Date(b.last_seen).getTime() : 0;
      }
      
      // Handle nulls
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      // Compare
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [agents, sortField, sortDirection]);

  const SortHeader = ({ field, children }) => (
    <th 
      onClick={() => handleSort(field)}
      style={{ 
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap'
      }}
    >
      {children}
      <span style={{ 
        marginLeft: '6px', 
        opacity: sortField === field ? 1 : 0.3,
        fontSize: '10px'
      }}>
        {sortField === field ? (sortDirection === 'asc' ? '▲' : '▼') : '▼'}
      </span>
    </th>
  );

  return (
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <SortHeader field="status">Status</SortHeader>
            <SortHeader field="agent_id">Agent ID</SortHeader>
            <SortHeader field="hostname">Hostname</SortHeader>
            <SortHeader field="username">Username</SortHeader>
            <SortHeader field="os_type">OS</SortHeader>
            <SortHeader field="external_ip">External IP</SortHeader>
            <SortHeader field="country">Country</SortHeader>
            <SortHeader field="last_seen">Last Seen</SortHeader>
            <SortHeader field="agent_version">Version</SortHeader>
            <th>Tasks</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedAgents.map((agent) => (
            <tr 
              key={agent.agent_id}
              onClick={() => navigate(`/agents/${agent.agent_id}`)}
              style={{ cursor: 'pointer' }}
            >
              <td>
                <span className={`status-dot ${agent.is_online ? 'online' : 'offline'}`}></span>
              </td>
              <td>
                <code style={{ color: 'var(--neon-cyan)' }}>
                  {agent.agent_id?.substring(0, 8)}...
                </code>
              </td>
              <td>{agent.hostname || 'Unknown'}</td>
              <td>{agent.username || 'Unknown'}</td>
              <td>
                <span style={{ marginRight: '8px' }}>{getOsIcon(agent.os_type)}</span>
                {agent.os_type}
              </td>
              <td>
                <code>{agent.external_ip}</code>
              </td>
              <td>
                {agent.country_code && (
                  <span>
                    {agent.country_code} {agent.city && `- ${agent.city}`}
                  </span>
                )}
              </td>
              <td>
                {agent.last_seen && (
                  <span style={{ color: agent.is_online ? 'var(--neon-green)' : 'var(--text-muted)' }}>
                    {formatDistanceToNow(new Date(agent.last_seen), { addSuffix: true })}
                  </span>
                )}
              </td>
              <td>
                <code style={{ 
                  fontSize: '11px',
                  color: agent.agent_version ? 'var(--neon-cyan)' : 'var(--text-muted)'
                }}>
                  {agent.agent_version || '?'}
                </code>
              </td>
              <td>
                {agent.pending_tasks > 0 && (
                  <span className="nav-item-badge">{agent.pending_tasks}</span>
                )}
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => navigate(`/agents/${agent.agent_id}`)}
                >
                  View
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => onDelete(agent.agent_id)}
                  style={{ marginLeft: '8px' }}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {agents.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: 'var(--text-muted)' 
        }}>
          No agents found. Start a listener and deploy an agent to begin.
        </div>
      )}
    </div>
  );
}

export default AgentTable;
